import { LuScanSearch, LuUsers } from 'react-icons/lu'

import compassIcon from '@/assets/img/icons/compass.svg'
import compassOutlineIcon from '@/assets/img/icons/compass-outline.svg'
import SvgImage from '@/components/misc/SvgImage'
import { useCompanyDashboardSidebarLinkTitles } from '@/hooks/useCompanyDashboardSidebarLinkTitles'
import { useNotificationsCount } from '@/hooks/useNotificationsCount'
import { ROUTES } from '@/lib/routes'
import { SidebarLink } from '@/types/misc'

export function useCompanyDashboardSidebarLinks() {
  const notificationsCount = useNotificationsCount()
  const titles = useCompanyDashboardSidebarLinkTitles()
  const links: SidebarLink[] = []

  const dashboardLink: SidebarLink = {
    title: titles.dashboard,
    href: ROUTES.companyDashboard(),
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7.5" height="7.5" rx="2"></rect>
        <rect x="13.5" y="3" width="7.5" height="7.5" rx="2"></rect>
        <rect x="3" y="13.5" width="7.5" height="7.5" rx="2"></rect>
        <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2"></rect>
      </svg>
    ),
  }

  const referralsLink: SidebarLink = {
    title: titles.referrals,
    href: ROUTES.referrals(),
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 8h12"></path>
        <path d="M12 4l4 4-4 4"></path>
        <path d="M20 16H8"></path>
        <path d="M12 20l-4-4 4-4"></path>
      </svg>
    ),
    badge:
      notificationsCount.referrals > 0
        ? `${notificationsCount.referrals}`
        : undefined,
  }

  const professionalNetworkLink: SidebarLink = {
    title: titles.yourNetwork,
    href: ROUTES.professionalNetworkRelationships(),
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="4" cy="15" r="2.5"></circle>
        <circle cx="12" cy="12" r="3"></circle>
        <circle cx="20" cy="5" r="2.5"></circle>
        <circle cx="20" cy="19" r="2.5"></circle>
        <path d="M6.5 14.5l3-1.5"></path>
        <path d="M15 10.5l3-3.5"></path>
        <path d="M15 13.5l3 3.5"></path>
      </svg>
    ),
    badge:
      notificationsCount.professionalNetwork > 0
        ? `${notificationsCount.professionalNetwork}`
        : undefined,
  }

  const yourClientsLink: SidebarLink = {
    title: titles.yourClients,
    href: ROUTES.clients(),
    icon: <LuUsers />,
  }

  const recommendations: SidebarLink = {
    title: titles.recommendations,
    href: ROUTES.recommendations(),
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="6.5" r="3"></circle>
        <path d="M6 21v-1c0-3.3 2.7-6 6-6s6 2.7 6 6v1"></path>
        <circle cx="5" cy="9" r="2" opacity="0.5"></circle>
        <path d="M3 21v-.5c0-2.2 1-3.8 2.5-4.5" opacity="0.5"></path>
        <circle cx="19" cy="9" r="2" opacity="0.5"></circle>
        <path d="M21 21v-.5c0-2.2-1-3.8-2.5-4.5" opacity="0.5"></path>
      </svg>
    ),
  }

  const compassLink: SidebarLink = {
    title: titles.compass,
    href: ROUTES.companyCompassDashboard(),
    icon: <SvgImage src={compassOutlineIcon} alt={titles.compass} />,
    activeIcon: <SvgImage src={compassIcon} alt={titles.compass} />,
  }

  const referralDiscoveryLink: SidebarLink = {
    title: titles.referralDiscovery,
    href: ROUTES.referralDiscovery(),
    icon: <LuScanSearch className="size-4" />,
    badge: 'Beta',
    badgeVariant: 'success-light',
    isHoverOnlyWhenCollapsed: true,
  }

  const integrationsLink: SidebarLink = {
    title: titles.integrations,
    href: '#',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="7" cy="7" r="2.5"></circle>
        <circle cx="17" cy="7" r="2.5"></circle>
        <circle cx="7" cy="17" r="2.5"></circle>
        <circle cx="17" cy="17" r="2.5"></circle>
        <path d="M9.5 7h5"></path>
        <path d="M7 9.5v5"></path>
        <path d="M17 9.5v5"></path>
        <path d="M9.5 17h5"></path>
      </svg>
    ),
    comingSoonText: 'Integrations are coming soon',
  }

  links.push(
    dashboardLink,
    referralsLink,
    referralDiscoveryLink,
    professionalNetworkLink,
    yourClientsLink,
    recommendations,
    compassLink,
    integrationsLink,
  )

  return links
}
